import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import Button from '../ui/Button';
import fieldWorkService from '../../services/fieldWork';
import {
  workerDisplayName,
  type Crew,
  type LabourProvider,
  type Worker,
} from '../../types/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import FormModal, { Field, inputClass } from './FormModal';

/**
 * Crews: a leader and the people they run. Dropping someone from a crew keeps
 * when they were in it, so later hours and bins still cost to the right crew.
 */
export default function CrewsPanel({ providers }: { providers: LabourProvider[] }) {
  const [crews, setCrews] = useState<Crew[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Crew | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, w] = await Promise.all([fieldWorkService.getCrews(true), fieldWorkService.getWorkers()]);
      setCrews(c);
      setWorkers(w);
    } catch (e) {
      setError(errorMessage(e, 'Could not load crews'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-neutral-500">Group people so a leader can start and stop the whole crew on a job.</p>
        <Button size="sm" variant="primary" onClick={() => setEditing('new')} disabled={workers.length === 0}>
          <Plus className="h-4 w-4" /> New crew
        </Button>
      </div>
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
      {loading ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Loading crews…</p>
      ) : crews.length === 0 ? (
        <p className="text-sm text-neutral-500 py-10 text-center">
          {workers.length === 0 ? 'Add workers to the roster first, then group them into crews.' : 'No crews yet.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {crews.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => setEditing(c)}
              className={`text-left border rounded-lg p-4 hover:shadow-sm transition-shadow ${c.active ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50'}`}
            >
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                <span className="font-medium text-neutral-900 truncate">{c.name}</span>
                {c.season_year && <span className="text-xs text-neutral-500">{c.season_year}</span>}
                {!c.active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Inactive</span>}
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                {c.members.length} {c.members.length === 1 ? 'person' : 'people'}
                {c.leader_name && ` · led by ${c.leader_name}`}
                {c.labour_provider_name && ` · ${c.labour_provider_name}`}
              </p>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <CrewFormModal
          crew={editing === 'new' ? null : editing}
          workers={workers}
          providers={providers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CrewFormModal({ crew, workers, providers, onClose, onSaved }: {
  crew: Crew | null;
  workers: Worker[];
  providers: LabourProvider[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(crew?.name ?? '');
  const [providerId, setProviderId] = useState<number | null>(crew?.labour_provider_id ?? null);
  const [leaderId, setLeaderId] = useState<number | null>(crew?.leader_worker_id ?? null);
  const [season, setSeason] = useState<string>(crew?.season_year ? String(crew.season_year) : '');
  const [members, setMembers] = useState<Set<number>>(new Set(crew?.members.map(m => m.worker_id) ?? []));
  const [active, setActive] = useState(crew?.active ?? true);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return workers.filter(w => !q || `${workerDisplayName(w)} ${w.picker_code}`.toLowerCase().includes(q));
  }, [workers, filter]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      name: name.trim(),
      labour_provider_id: providerId,
      leader_worker_id: leaderId,
      season_year: season ? Number(season) : null,
      member_worker_ids: [...members],
      ...(crew ? { active } : {}),
    };
    try {
      if (crew) await fieldWorkService.updateCrew(crew.id, payload);
      else await fieldWorkService.createCrew(payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the crew'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      title={crew ? 'Edit crew' : 'New crew'}
      onClose={onClose}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={!name.trim()}
      error={error}
    >
      <Field label="Name" htmlFor="crew-name">
        <input id="crew-name" className={inputClass} value={name} onChange={e => setName(e.target.value)} autoFocus />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Leader" htmlFor="crew-leader" hint="The leader is always a member.">
          <select id="crew-leader" className={inputClass} value={leaderId ?? ''}
            onChange={e => setLeaderId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">No leader</option>
            {workers.map(w => <option key={w.id} value={w.id}>{workerDisplayName(w)}</option>)}
          </select>
        </Field>
        <Field label="Season (optional)" htmlFor="crew-season">
          <input id="crew-season" type="number" inputMode="numeric" className={inputClass} value={season}
            placeholder="e.g. 2026" onChange={e => setSeason(e.target.value)} />
        </Field>
      </div>
      <Field label="Labour provider" htmlFor="crew-provider">
        <select id="crew-provider" className={inputClass} value={providerId ?? ''}
          onChange={e => setProviderId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">None (our own crew)</option>
          {providers.filter(p => p.active || p.id === providerId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-neutral-700">Members ({members.size})</span>
        </div>
        <input aria-label="Filter workers" className={`${inputClass} mb-2`} placeholder="Filter by name or code"
          value={filter} onChange={e => setFilter(e.target.value)} />
        <ul className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-60 overflow-y-auto">
          {shown.map(w => (
            <li key={w.id}>
              <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={members.has(w.id) || w.id === leaderId}
                  disabled={w.id === leaderId}
                  onChange={() => setMembers(s => {
                    const n = new Set(s);
                    if (n.has(w.id)) n.delete(w.id); else n.add(w.id);
                    return n;
                  })}
                  className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200"
                />
                <span className="text-neutral-800 truncate">{workerDisplayName(w)}</span>
                <span className="text-xs font-mono text-neutral-500 ml-auto">{w.picker_code}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      {crew && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-700">Active</span>
        </label>
      )}
    </FormModal>
  );
}
